// src/app/interceptors/auth.interceptor.ts
import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpClient,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, switchMap, take, map, finalize, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TokenService } from '../service/token.service';
import { MatDialog } from '@angular/material/dialog';
import { SessionExpiredComponent } from '../components/session-expired/session-expired.component';
import { applicationConfig } from '../../environments/environment';

// Module-level state (singleton per app instance — same behaviour as the class's private fields)
let isDialogOpen = false;
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function openSessionExpired(
  dialog: MatDialog,
  router: Router
): Observable<never> {
  if (!isDialogOpen) {
    isDialogOpen = true;
    const dialogRef = dialog.open(SessionExpiredComponent, { disableClose: true });
    dialogRef.afterClosed().subscribe(() => {
      isDialogOpen = false;
      localStorage.clear();
      router.navigate(['/login']);
    });
  }
  return throwError(() => new Error('Unauthorized'));
}

function refreshAccessToken(http: HttpClient): Observable<string | null> {
  const { domain, clientId, clientSecret } = (applicationConfig as any)?.cambium || {};
  if (!domain || !clientId) {
    return of(null);
  }

  const tokenUrl = `https://${domain}/protocol/openid-connect/token`;
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: localStorage.getItem('refreshToken') ?? '',
  });
  if (clientSecret) {
    body.set('client_secret', clientSecret);
  }

  return http
    .post<any>(tokenUrl, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    })
    .pipe(
      timeout(15000),
      map((resp) => {
        const access = resp?.access_token as string | undefined;
        const id = resp?.id_token as string | undefined;
        const refresh = resp?.refresh_token as string | undefined;
        if (access) {
          localStorage.setItem('accessToken', access);
        }
        if (id) {
          localStorage.setItem('idToken', id);
        }
        if (refresh) {
          localStorage.setItem('refreshToken', refresh);
        }
        return access || null;
      }),
      catchError(() => of(null)),
      finalize(() => {
        isRefreshing = false;
      })
    );
}

function handle401Error(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  http: HttpClient,
  dialog: MatDialog,
  router: Router
): Observable<HttpEvent<any>> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return openSessionExpired(dialog, router);
  }

  if (isRefreshing) {
    return refreshTokenSubject.pipe(
      take(1),
      switchMap((token) => {
        if (!token) {
          return openSessionExpired(dialog, router);
        }
        const headers = req.headers
          .set('Authorization', `Bearer ${token}`)
          .set('X-Refresh-Retry', '1');
        return next(req.clone({ headers }));
      })
    );
  }

  isRefreshing = true;
  refreshTokenSubject.next(null);

  return refreshAccessToken(http).pipe(
    switchMap((newAccessToken: string | null) => {
      if (!newAccessToken) {
        refreshTokenSubject.next('');
        return openSessionExpired(dialog, router);
      }
      refreshTokenSubject.next(newAccessToken);
      const headers = req.headers
        .set('Authorization', `Bearer ${newAccessToken}`)
        .set('X-Refresh-Retry', '1');
      return next(req.clone({ headers }));
    }),
    catchError(() => {
      refreshTokenSubject.next('');
      return openSessionExpired(dialog, router);
    }),
    take(1)
  ) as Observable<HttpEvent<any>>;
}

export const authInterceptorFn: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const router       = inject(Router);
  const tokenService = inject(TokenService);
  const dialog       = inject(MatDialog);
  const http         = inject(HttpClient);

  const idToken: any = tokenService.getToken();
  const accessToken  = localStorage.getItem('accessToken');
  const userId       = localStorage.getItem('userId');
  const sessionId    = localStorage.getItem('sessionId');

  // Determine safe routes/requests that should not require an ID token
  const isLoginRoute  = router.url?.includes('/login');
  const isAuthRequest = typeof req.url === 'string' && (
    req.url.includes('/protocol/openid-connect/token') ||
    req.url.includes('/protocol/openid-connect/auth') ||
    req.url.includes('/auth/realms')
  );
  // Allow API gateway DB login call without requiring token
  const isDbLoginRequest = req.method === 'POST'
    && typeof req.body === 'object'
    && req.body?.resource === '/users/login';

  // If no ID token and not on login route and not an auth-related request, redirect to login
  if (!idToken && !isLoginRoute && !isAuthRequest && !isDbLoginRequest) {
    router.navigate(['/login']);
    return throwError(() => new Error('No authentication token found'));
  }

  let headers = req.headers;

  // Skip adding any auth/custom headers for SSO auth endpoints
  if (!isAuthRequest) {
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }
  }

  // Only attach custom identification headers for same-origin requests to avoid CORS issues
  let isSameOrigin = false;
  try {
    const resolvedUrl = new URL(req.url, window.location.origin);
    isSameOrigin = resolvedUrl.origin === window.location.origin;
  } catch {
    isSameOrigin = false;
  }

  if (!isAuthRequest) {
    if (isSameOrigin && userId) {
      headers = headers.set('User-Id', userId);
    }
    if (isSameOrigin && sessionId) {
      headers = headers.set('Session-Id', sessionId);
    }
  }

  const clonedReq = req.clone({ headers });

  // Log outgoing request (method and URL only)
  try {
    console.debug('[HTTP Request]', clonedReq.method, clonedReq.url);
  } catch {}

  return next(clonedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Centralized error logging to avoid silent failures
      try {
        const status = error.status;
        const url    = clonedReq.url;
        const method = clonedReq.method;
        if (status === 0) {
          console.error('[HTTP Error]', method, url, 'Status: 0 (Network/CORS error)', error.message);
        } else {
          console.error('[HTTP Error]', method, url, 'Status:', status, 'Message:', error.message);
        }
      } catch {}

      if (error.status === 401) {
        const alreadyRetried = clonedReq.headers.get('X-Refresh-Retry') === '1';
        if (isAuthRequest) {
          return throwError(() => error);
        }
        if (alreadyRetried) {
          return openSessionExpired(dialog, router);
        }
        return handle401Error(clonedReq, next, http, dialog, router);
      }
      return throwError(() => error);
    })
  );
};
